import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getAllUsers, disableUser } from 'services/userService';

function UserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null });

  const handleDisableClick = (e, user) => {
    e.stopPropagation();
    if (user.enabled === false || user.Enabled === false) return;
    setConfirmDialog({ open: true, user });
  };

  const handleDisableConfirm = async () => {
    const { user } = confirmDialog;
    setConfirmDialog({ open: false, user: null });
    const username = user.userName || user.user_name;
    try {
      await disableUser(username);
      setSnackbar({ open: true, message: 'User disabled', severity: 'success' });
      await loadUsers();
    } catch (err) {
      console.error('Error disabling user:', err);
      setSnackbar({ open: true, message: 'Failed to disable user', severity: 'error' });
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading users:', err);
      setSnackbar({ open: true, message: 'Failed to load users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6">Users</SoftTypography>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={() => navigate('/users/create')}
              >
                Add User
              </SoftButton>
            </SoftBox>
            <SoftBox>
              {loading ? (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress />
                </SoftBox>
              ) : users.length === 0 ? (
                <SoftBox p={3}>
                  <Alert severity="info">No users found.</Alert>
                </SoftBox>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Modified</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Enabled</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user, index) => (
                        <TableRow
                          key={user.userName || user.user_name || index}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            const username = user.userName || user.user_name;
                            const email = user.email || '';
                            const tenantId = user.tenant_id || user.tenantId || '';
                            navigate(`/users/edit?username=${encodeURIComponent(username)}&email=${encodeURIComponent(email)}&tenantId=${encodeURIComponent(tenantId)}`);
                          }}
                        >
                          <TableCell>
                            <SoftTypography variant="caption" fontWeight="medium">
                              {user.email || user.userName || user.user_name}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {formatDate(user.created || user.UserCreateDate)}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {formatDate(user.modified || user.UserLastModifiedDate)}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {user.status || user.UserStatus || '-'}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.enabled !== false && user.Enabled !== false ? 'Yes' : 'No'}
                              size="small"
                              color={user.enabled !== false && user.Enabled !== false ? 'success' : 'default'}
                              onClick={(e) => handleDisableClick(e, user)}
                              clickable={user.enabled !== false && user.Enabled !== false}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, user: null })}
      >
        <DialogTitle>Disable User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to disable{' '}
            <strong>{confirmDialog.user?.email || confirmDialog.user?.userName || confirmDialog.user?.user_name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, user: null })}>Cancel</Button>
          <Button onClick={handleDisableConfirm} color="error" variant="contained">
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default UserList;
